import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Check, Pencil, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

function TaxRow({ item, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const save = async () => {
    if (!name.trim()) return;
    try { await api.put(`/admin/taxonomy/${item.id}`, { name: name.trim() }); setEditing(false); onSaved(); toast.success("Updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async () => {
    try { await api.delete(`/admin/taxonomy/${item.id}`); onSaved(); toast.success("Deleted"); }
    catch { toast.error("Failed to delete"); }
  };
  return (
    <div className="flex items-center gap-2 py-2 border-b border-border last:border-0" data-testid={`tax-row-${item.name}`}>
      {editing ? (
        <>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 rounded-sm flex-1" />
          <button onClick={save} className="text-foreground p-1" data-testid={`tax-save-${item.name}`}><Check className="h-4 w-4" strokeWidth={1.5} /></button>
          <button onClick={() => { setEditing(false); setName(item.name); }} className="text-muted-foreground p-1"><X className="h-4 w-4" strokeWidth={1.5} /></button>
        </>
      ) : (
        <>
          <span className="flex-1 text-sm">{item.name}</span>
          <button onClick={() => setEditing(true)} className="text-muted-foreground hover:text-foreground p-1" data-testid={`tax-edit-${item.name}`}><Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /></button>
          <button onClick={del} className="text-muted-foreground hover:text-destructive p-1" data-testid={`tax-delete-${item.name}`}><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /></button>
        </>
      )}
    </div>
  );
}

function AddRow({ onAdd, placeholder, testid }) {
  const [val, setVal] = useState("");
  const add = () => { if (!val.trim()) return; onAdd(val.trim()); setVal(""); };
  return (
    <div className="flex gap-2 mt-3">
      <Input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder={placeholder} className="h-9 rounded-sm" data-testid={`${testid}-input`} />
      <Button size="sm" className="rounded-sm bg-foreground text-background hover:bg-foreground/90" onClick={add} data-testid={`${testid}-add`}>
        <Plus className="h-4 w-4" strokeWidth={1.5} />
      </Button>
    </div>
  );
}

export default function TaxonomyManager() {
  const [items, setItems] = useState([]);
  const [subCat, setSubCat] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/taxonomy"); setItems(data); }
    catch { toast.error("Failed to load taxonomy"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (kind, name, parent = null) => {
    try { await api.post("/admin/taxonomy", { kind, name, parent }); load(); toast.success("Added"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); }
  };

  const types = items.filter((i) => i.kind === "type");
  const categories = items.filter((i) => i.kind === "category");
  const subs = items.filter((i) => i.kind === "subcategory" && (!subCat || i.parent === subCat));
  const catNames = categories.map((c) => c.name);

  const Panel = ({ title, children }) => (
    <div className="border border-border rounded-sm p-4">
      <h3 className="font-serif text-2xl tracking-tight mb-2">{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="grid gap-6 md:grid-cols-3" data-testid="taxonomy-manager">
      <Panel title="Types">
        <div>{types.map((t) => <TaxRow key={t.id} item={t} onSaved={load} />)}</div>
        <AddRow onAdd={(n) => add("type", n)} placeholder="New type" testid="tax-type" />
      </Panel>

      <Panel title="Categories">
        <div>{categories.map((c) => <TaxRow key={c.id} item={c} onSaved={load} />)}</div>
        <AddRow onAdd={(n) => add("category", n)} placeholder="New category" testid="tax-category" />
      </Panel>

      <Panel title="Sub-categories">
        <Select value={subCat || "__all"} onValueChange={(v) => setSubCat(v === "__all" ? "" : v)}>
          <SelectTrigger className="h-9 rounded-sm mb-3" data-testid="tax-sub-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All categories</SelectItem>
            {catNames.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div>{subs.map((s) => (
          <div key={s.id} className="flex items-center gap-2">
            <span className="text-[0.65rem] text-muted-foreground w-20 shrink-0 truncate">{s.parent}</span>
            <div className="flex-1"><TaxRow item={s} onSaved={load} /></div>
          </div>
        ))}</div>
        <SubAdd catNames={catNames} defaultCat={subCat} onAdd={(name, parent) => add("subcategory", name, parent)} />
      </Panel>
    </div>
  );
}

function SubAdd({ catNames, defaultCat, onAdd }) {
  const [val, setVal] = useState("");
  const [parent, setParent] = useState(defaultCat || catNames[0] || "");
  useEffect(() => { if (defaultCat) setParent(defaultCat); }, [defaultCat]);
  const add = () => { if (!val.trim() || !parent) { toast.error("Pick a category and enter a name"); return; } onAdd(val.trim(), parent); setVal(""); };
  return (
    <div className="flex flex-col gap-2 mt-3">
      <Select value={parent} onValueChange={setParent}>
        <SelectTrigger className="h-9 rounded-sm" data-testid="tax-sub-parent"><SelectValue placeholder="Category" /></SelectTrigger>
        <SelectContent>{catNames.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
      </Select>
      <div className="flex gap-2">
        <Input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="New sub-category" className="h-9 rounded-sm" data-testid="tax-sub-input" />
        <Button size="sm" className="rounded-sm bg-foreground text-background hover:bg-foreground/90" onClick={add} data-testid="tax-sub-add">
          <Plus className="h-4 w-4" strokeWidth={1.5} />
        </Button>
      </div>
    </div>
  );
}
